import map from '@tinkoff/utils/object/map'
import { createEffect } from 'effector/effector.umd'
import { CarViewSettings, Dealer, MarketingColor, ProductTypeProps, VideoProps } from 'features/car/types'
import { fetchCarsOnListFx, Order, setFirstLoadPage, setRelatedFilter, SortType } from 'features/cars-list/model'
import { CreditFilterRequestParameters } from 'features/credit-filter-form/types'
import {
  FilterDeclaration,
  FilterFieldCheckbox,
  FilterFieldColorSelect,
  FilterFieldSwitch,
  FilterValues,
  resetValues
} from 'features/filter/model'
import { Reference } from 'lib/types'
import { stringifyUrl } from 'query-string'

import { AppTheme } from '../../application/theme'
import { prepareCarData } from '../../features/car/normalization'
import { listRawCarDataToRawCarData } from '../../features/car/normalization/listRawCarDataToRawCarData'
import { cloneDeclarationSection } from '../../lib/clone-declaration-section'
import { getCurrentUrl } from '../../lib/get-current-url'
import { logger } from '../../lib/logger'
import { AVNStageProps } from '../../stages/AVN/types'
import { post } from '../index'

export const getCarsCount = ({ url, filter, cities }: GetCarsCountRequest) => {
  return post<GetCarsCountResponse>(url, { data: { ...filter, cities } })
}

export type LogisticStatuses = 'on_the_way' | 'in_the_central_warehouse' | 'in_production' | 'in_stock'
export type ProductStatuses = 'active' | 'not_active' | 'reserved' | 'sold'

export type LogisticStatus = {
  id: LogisticStatuses
  name: string
}

export type ProductStatus = {
  id: ProductStatuses
  name: string
}

export type FetchCarsFxProps = {
  appTheme: AppTheme
  pageLength: number
  baseUrl: string
  carViewSettings: CarViewSettings
  sorting: { sort: SortType | null; order: Order | null } | null
  filter: FilterValues | null
  defaultFilterValues?: FilterValues | null
  permanentFilterValues: FilterValues | null
  pagesLoaded: number
  city: string[]
  creditFilter?: CreditFilterRequestParameters
  firstLoadPage: boolean
  filterWithDefaultValue: boolean
  filterDeclaration: FilterDeclaration | null
  settingsRelatedFilter?: any
  showCarsNextDealer?: boolean
}

export const getCarsFx = createEffect(async (options: FetchCarsFxProps) => {
  const {
    filter,
    sorting,
    pagesLoaded,
    city,
    baseUrl,
    pageLength,
    carViewSettings,
    appTheme,
    creditFilter,
    permanentFilterValues,
    defaultFilterValues,
    firstLoadPage,
    filterWithDefaultValue,
    filterDeclaration,
    settingsRelatedFilter,
    showCarsNextDealer
  } = options

  const { sort, order } = sorting || {}

  let sortValue = showCarsNextDealer ? `${sort},dealers` : sort

  let getUrl = stringifyUrl(
    {
      url: baseUrl,
      query: {
        sort_by: sortValue,
        order_by: order ? order : undefined,
        limit: pageLength * pagesLoaded,
        offset: 0
      }
    },
    { skipNull: true, skipEmptyString: true }
  )

  getUrl = getUrl.replace(/%2C/g, ',')

  let fromLocalDealer: any = {}
  if (getCurrentUrl().get('from_local_dealer')) {
    fromLocalDealer = { fromLocalDealer: getCurrentUrl().get('from_local_dealer')?.split(',') }
  }

  let filterWithPermanentValues: FilterValues = {}

  if (filter) {
    if (permanentFilterValues) {
      filterWithPermanentValues = {
        ...permanentFilterValues,
        ...fromLocalDealer,
        ...map((value, key) => {
          if (value.length === 0 && key in permanentFilterValues) {
            return permanentFilterValues[key]
          } else {
            return value
          }
        }, filter)
      }
    } else {
      filterWithPermanentValues = { ...filter, ...fromLocalDealer }
    }
  } else if (permanentFilterValues) {
    filterWithPermanentValues = {
      ...permanentFilterValues
    }
  }

  if (settingsRelatedFilter?.isRelatedFilter) {
    filterWithPermanentValues.filterOptions = settingsRelatedFilter.filterOptions
  }

  let body = {
    data: {
      ...filterWithPermanentValues,
      city: getCurrentUrl().get('from_local_dealer') ? [] : city,
      credit_filter: creditFilter ? { ...creditFilter } : undefined
    }
  }

  let data = await post<GetCarsResponse>(getUrl, body)

  if (data.cars.length !== 0) {
    /*Если машины есть ,то считаем что это не первая загрузка */
    setFirstLoadPage(false)
  }

  const filterRawValues: { [key: string]: { [key: string]: string } } = {}
  const filterData: { [key: string]: any } = {}

  if (filterDeclaration && filterDeclaration.sections) {
    for (const section of filterDeclaration.sections) {
      filterRawValues[section.key] = {}

      for (const group of section.groups) {
        if (section.type === 'checkbox') {
          for (const field of group.fields as FilterFieldCheckbox[]) {
            if (!field.value || !field.label) continue
            filterRawValues[section.key][field.value] = field.label
          }
        } else if (section.type === 'switch') {
          for (const field of group.fields as FilterFieldSwitch[]) {
            if (!field.value || !field.label) continue
            filterRawValues[section.key][field.value] = field.label
          }
        } else if (section.type === 'color-select') {
          for (const field of group.fields as FilterFieldColorSelect[]) {
            if (!field.value || !field.colorName) continue
            filterRawValues[section.key][field.value] = field.colorName
          }
        }
      }
    }
  }

  for (const key of Object.keys(filterWithPermanentValues)) {
    const values = filterRawValues[key]
    if (values) {
      const out: (string | number)[] = []

      for (const value of filterWithPermanentValues[key]) {
        if (values[value]) {
          out.push(values[value])
        } else {
          out.push(value)
        }
      }

      filterData[key] = out
    } else {
      filterData[key] = filterWithPermanentValues[key]
    }
  }

  document.dispatchEvent(
    // prettier-ignore
    new CustomEvent('kodix:ecom:carsLoaded', { detail: { filter: filterData, count: data.count }})
  )

  if (data.cars.length === 0 && defaultFilterValues && defaultFilterValues['brand'].length > 0 && firstLoadPage) {
    setFirstLoadPage(false)
    if (filterWithDefaultValue) {
      for (let key in filterWithPermanentValues) {
        if (key === 'brand') filterWithPermanentValues[key] = []
      }
      resetValues()
    }
    body = {
      data: { ...filterWithPermanentValues, city, credit_filter: creditFilter ? { ...creditFilter } : undefined }
    }
    data = await post<GetCarsResponse>(getUrl, body)
  }

  if (data?.relatedFilter?.length !== 0) {
    setRelatedFilter(data.relatedFilter)
  }

  return {
    cars: data.cars.map((car) => {
      //todo: вынести на уровень prepare car data но преед этим предётся поменять формат baseCarModel в деталке
      return prepareCarData(listRawCarDataToRawCarData(car), carViewSettings, appTheme, filter)
    }),
    total_count: data.count,
    minPrice: data.minPrice,
    maxPrice: data.maxPrice,
    minFilteredPrice: data.minFilteredPrice
  }
})

getCarsFx.failData.watch((error) => logger.error('Catched in effect:' + error))

// запрос по дилерам, которые передали в параметр функции

export const getCarsByDealerIds = async (
  listReqData: FetchCarsFxProps,
  filters: FilterValues,
  dealersIds: (string | number)[]
) => {
  return await fetchCarsOnListFx({
    ...listReqData,
    filter: { ...filters, dealer: dealersIds },
    city: []
  })
}

// запрос со всеми дилерами
export const getCarsByAllDealers = async (props: AVNStageProps, dealers: string[], filters: FilterValues | null) => {
  if (filters) {
    const formattedUrl = stringifyUrl(
      {
        url: props.getUrl,
        query: {
          sort_by: props.sorting?.options[0]?.key || 'price',
          order_by: props.sorting?.options[0]?.order || 'asc',
          limit: props.pageLength,
          offset: 0
        }
      },
      { skipNull: true, skipEmptyString: true }
    )

    // собираем объект для запроса на машины
    const body = {
      data: {
        ...filters,
        dealer: [...dealers],
        city: [],
        credit_filter: props.creditFilterForm ? { ...props.creditFilterForm } : undefined
      }
    }

    return await post<GetCarsResponse>(formattedUrl, body)
  }

  return null
}

export type ListRawCarData = {
  attributes: {
    ext_id: string
    year: string
    url_detail: string
    vin: string
    vin_raw: string
    price: number | null
    price_with_discounts: number | null
    additional_options_cost: number | null
    quantity: number
    transmission: Reference
    dealers: Dealer[]
    fuel: Reference<{ name: string }>
    cities: { name: string }[]
    logistic_status: LogisticStatus
    status?: ProductStatus
    wheel_drive: Reference
    marketing_complectation: Reference<{ code: string }>
    complectation: Reference
    model: Reference<{ name: string }>
    brand: Reference<{ name: string }>
    restyling: Reference<{ name: string }>
    stock_images_id: string[]
    dealer_images_id: string[]
    videos?: VideoProps[]
    labels: Reference<{
      color: string
      text: {
        value: string
      }
      tooltip: {
        value: string
      }
      value: string
      hidden?: boolean
    }>[]
    color: Reference
    base_color: { hex: string; name: string }
    generation: Reference<{ name: string }>
    upholstery: { name: string; material: string }
    enginePowerHorsePower: number
    is_new: boolean
    mileage: number
    marketingColors: MarketingColor[]
    tradeinDiscount: number // todo: прокинуть в CarData и далее в смарты
    creditDiscount: number
    insuranceDiscount: number
    creditTradeinDiscount: number
    collapsedCars?: { identity: string; dealerId: string }[] | null
    credit_price?: number
    placeholders: Record<string, string>
    credit_info?: {
      monthly_payment?: number
    }
    has_video: boolean
  }
  id: string
  type: 'car'
}

/**
 * Данные, присылаемые бэкендом из базы данных - то, что приходит по запросу из  API
 * Эти данные будут нормализованы перед выводом в соответствии с настройками, задаными в {@link CarViewSettings}
 */

export interface RawCarData {
  id: string
  type: 'car'
  ext_id: string
  year: string
  url_detail?: string
  vin: string
  vin_raw: string
  price: number | null
  price_with_discounts: number | null
  additional_options_cost: number | null
  quantity?: number
  transmission: Reference
  dealers: Dealer[]
  fuel: Reference<{ name: string }>
  cities: { name: string }[]
  logistic_status: LogisticStatus
  status?: ProductStatus
  wheel_drive: Reference
  marketing_complectation: Reference<{ code: string }>
  complectation: Reference
  model: Reference<{ name: string }>
  brand: Reference<{ name: string }>
  restyling: Reference<{ name: string }>
  productType: ProductTypeProps
  body?: string
  stock_images_id: string[]
  dealer_images_id: string[]
  videos?: VideoProps[]
  labels: Reference<{
    color: string
    text: string
    tooltip: string
    hidden?: boolean
  }>[]
  color?: Reference
  base_color: { hex: string; name: string }
  generation: Reference<{ name: string }>
  upholstery: { name: string; material: string }
  enginePowerHorsePower: number
  mileage: number
  marketingColors: MarketingColor[]
  tradeinDiscount: number // todo: прокинуть в CarData и далее в смарты
  creditDiscount: number
  insuranceDiscount: number
  creditTradeinDiscount: number
  placeholders: Record<string, string>
  collapsedCars?: { identity: string; dealerId: string }[] | null
  credit_info?: {
    monthly_payment?: number
  }
  has_video?: boolean
  interior360?: string
  exterior360?: any
  reportVIN?: ReportVin
  has_autoteka_report?: boolean
}

export type GetCarsResponse = {
  cars: ListRawCarData[]
  count: number
  maxPrice: number
  minPrice: number
  minFilteredPrice: number
  relatedFilter?: []
}

export type GetCarsRequest = {
  url: string
  filter?: FilterValues
  order?: Order
  sort?: SortType
  limit?: number
  offset?: number
  city?: string[]
  creditFilter?: CreditFilterRequestParameters & { widget_id: string }
}

export type GetCarsCountResponse = {
  cards?: any[]
  count?: number
  tabs?: any[]
}

export type GetCarsCountRequest = {
  url: string
  filter: FilterValues
  cities: string[]
}

export type ReportVin = {
  text: string
  link: string
}
